import { SackKronerIcon } from '@navikt/aksel-icons';
import { ContextDataType, useContextGetData, useContextSaveData } from 'appData/PlanleggerDataContext';
import { PlanleggerRoutes } from 'appData/routes';
import { usePlanleggerNavigator } from 'appData/usePlanleggerNavigator';
import { useStepData } from 'appData/useStepData';
import { PlanleggerStepPage } from 'components/page/PlanleggerStepPage';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { HvorMye } from 'types/HvorMye';
import { erAlenesøker, getFornavnPåSøker1, getFornavnPåSøker2 } from 'utils/HvemPlanleggerUtils';
import { utledHvemSomHarRett } from 'utils/hvemHarRettUtils';
import { finnSisteGrunnbeløp } from 'utils/satserUtils';

import { BodyShort, Heading, Link, Spacer, VStack } from '@navikt/ds-react';

import { links } from '@navikt/fp-constants';
import { RhfForm, RhfFormattertTallTextField, StepButtonsHookForm } from '@navikt/fp-form-hooks';
import { Satser } from '@navikt/fp-types';
import { BluePanel, Infobox } from '@navikt/fp-ui';
import { useScrollBehaviour } from '@navikt/fp-utils';
import { isValidNumberForm, notEmpty } from '@navikt/fp-validation';

import { HarIkkeRettTilFpInfobox } from './infoboks/HarIkkeRettTilFpInfobox';
import { Utbetaling } from './infoboks/Utbetaling';

interface Props {
    satser: Satser;
}

export const HvorMyeSteg = ({ satser }: Props) => {
    const intl = useIntl();
    const navigator = usePlanleggerNavigator();
    const stepConfig = useStepData();

    const hvorMye = useContextGetData(ContextDataType.HVOR_MYE);
    const hvemPlanlegger = notEmpty(useContextGetData(ContextDataType.HVEM_PLANLEGGER));
    const arbeidssituasjon = notEmpty(useContextGetData(ContextDataType.ARBEIDSSITUASJON));
    const hvemHarRett = utledHvemSomHarRett(arbeidssituasjon);

    const kunSøker2HarRett = hvemHarRett === 'kunSøker2HarRett';
    const kunEnAvSøkereneHarRett = hvemHarRett === 'kunSøker1HarRett' || kunSøker2HarRett;
    const erAleneforsørger = erAlenesøker(hvemPlanlegger);

    const fornavnSøker1 = getFornavnPåSøker1(hvemPlanlegger, intl);
    const fornavnSøker2 = getFornavnPåSøker2(hvemPlanlegger, intl);

    const oppdaterHvorMye = useContextSaveData(ContextDataType.HVOR_MYE);

    const formMethods = useForm<HvorMye>({ defaultValues: hvorMye });

    const lønnSøker1 = formMethods.watch('lønnSøker1');
    const lønnSøker2 = formMethods.watch('lønnSøker2');

    const erValidLønn = (verdi: number | undefined): boolean => {
        if (!verdi) {
            return false;
        }
        const strVerdi = String(verdi);
        return strVerdi.length >= 3 && /^\d+$/.test(strVerdi);
    };

    const lønnSøker1Valid = erValidLønn(lønnSøker1);
    const lønnSøker2Valid = erValidLønn(lønnSøker2);

    const grunnbeløpet = finnSisteGrunnbeløp(satser);
    const minÅrslønn = Math.round(grunnbeløpet / 2);

    const årslønnSøker1 = Number(lønnSøker1) * 12;
    const årslønnSøker2 = Number(lønnSøker2) * 12;
    const harSøker1IkkeRettTilFp = årslønnSøker1 < minÅrslønn;
    const harSøker2IkkeRettTilFp = årslønnSøker2 < minÅrslønn;

    const lønnFørstePanel = kunSøker2HarRett ? lønnSøker2 : lønnSøker1;
    const lønnFørstePanelValid = kunSøker2HarRett ? lønnSøker2Valid : lønnSøker1Valid;
    const harFørstePanelIkkeRettTilFp = kunSøker2HarRett ? harSøker2IkkeRettTilFp : harSøker1IkkeRettTilFp;
    const årslønnFørstePanel = kunSøker2HarRett ? årslønnSøker2 : årslønnSøker1;
    const fornavnFørstePanel = kunSøker2HarRett ? fornavnSøker2 : fornavnSøker1;

    const onSubmit = (formValues: HvorMye) => {
        oppdaterHvorMye(formValues);
        navigator.goToNextStep(PlanleggerRoutes.HVOR_LANG_PERIODE);
    };

    const { ref } = useScrollBehaviour();

    return (
        <PlanleggerStepPage ref={ref} steps={stepConfig} goToStep={navigator.goToNextStep}>
            <RhfForm formMethods={formMethods} onSubmit={onSubmit} shouldUseFlexbox>
                <VStack gap="space-40" style={{ flex: 1 }}>
                    <VStack gap="space-24">
                        <Heading size="medium" spacing level="2">
                            <FormattedMessage id="HvorMyeSteg.Tittel" />
                        </Heading>
                        <VStack gap="space-8">
                            <BluePanel isDarkBlue={true}>
                                <RhfFormattertTallTextField
                                    name={kunSøker2HarRett ? 'lønnSøker2' : 'lønnSøker1'}
                                    control={formMethods.control}
                                    label={
                                        <FormattedMessage id="HvorMyeSteg.Lønn" values={{ hvem: fornavnFørstePanel }} />
                                    }
                                    validate={[
                                        isValidNumberForm(intl.formatMessage({ id: 'HvorMyeSteg.ValidNumber' })),
                                    ]}
                                    description={intl.formatMessage({ id: 'HvorMyeSteg.LønnBeskrivelse' })}
                                />
                            </BluePanel>
                            {lønnFørstePanelValid && harFørstePanelIkkeRettTilFp && (
                                <HarIkkeRettTilFpInfobox antattÅrslønn={årslønnFørstePanel} minÅrslønn={minÅrslønn} />
                            )}
                            {lønnFørstePanelValid && !harFørstePanelIkkeRettTilFp && (
                                <Utbetaling
                                    lønnSøker={Number(lønnFørstePanel)}
                                    satser={satser}
                                    fornavn={fornavnFørstePanel ?? ''}
                                />
                            )}
                        </VStack>
                        {!kunEnAvSøkereneHarRett && fornavnSøker2 && (
                            <VStack gap="space-8">
                                <BluePanel isDarkBlue={true}>
                                    <RhfFormattertTallTextField
                                        name="lønnSøker2"
                                        control={formMethods.control}
                                        label={
                                            <FormattedMessage
                                                id="HvorMyeSteg.Lønn"
                                                values={{ hvem: getFornavnPåSøker2(hvemPlanlegger, intl) }}
                                            />
                                        }
                                        validate={[
                                            isValidNumberForm(intl.formatMessage({ id: 'HvorMyeSteg.ValidNumber' })),
                                        ]}
                                        description={intl.formatMessage({ id: 'HvorMyeSteg.LønnBeskrivelse' })}
                                    />
                                </BluePanel>
                                {lønnSøker2Valid && harSøker2IkkeRettTilFp && (
                                    <HarIkkeRettTilFpInfobox antattÅrslønn={årslønnSøker2} minÅrslønn={minÅrslønn} />
                                )}
                                {lønnSøker2Valid && !harSøker2IkkeRettTilFp && (
                                    <Utbetaling
                                        satser={satser}
                                        lønnSøker={Number(lønnSøker2)}
                                        fornavn={fornavnSøker2}
                                    />
                                )}
                            </VStack>
                        )}
                        <VStack gap="space-8">
                            <Infobox
                                header={<FormattedMessage id="HvorMyeSteg.ViteMer" values={{ erAleneforsørger }} />}
                                icon={
                                    <SackKronerIcon
                                        height={24}
                                        width={24}
                                        color="var(--ax-bg-neutral-strong)"
                                        fontSize="1.5rem"
                                        aria-hidden
                                    />
                                }
                                color="gray"
                            >
                                <BodyShort>
                                    <FormattedMessage
                                        id="HvorMyeSteg.MerDetaljert"
                                        values={{
                                            a: (msg) => (
                                                <Link href={links.hvorMye} target="_blank" rel="noreferrer" inlineText>
                                                    {msg}
                                                </Link>
                                            ),
                                        }}
                                    />
                                </BodyShort>
                            </Infobox>
                        </VStack>
                    </VStack>

                    <Spacer />
                    <StepButtonsHookForm<HvorMye>
                        saveDataOnPreviousClick={oppdaterHvorMye}
                        goToPreviousStep={navigator.goToPreviousDefaultStep}
                        useSimplifiedTexts
                    />
                </VStack>
            </RhfForm>
        </PlanleggerStepPage>
    );
};
